import React from "react";
import { Tooltip } from "react-tooltip";
import { RoleValidationConfig } from "../../../Models";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface ConfigInputProps {
  label: string;
  tooltipText: string;
  id: keyof Omit<RoleValidationConfig, "id" | "roleId">;
  value: number | null | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step?: number;
}

const ConfigInput: React.FC<ConfigInputProps> = ({
  label,
  tooltipText,
  id,
  value,
  onChange,
  ...props
}) => {
  return (
    <div className="flex flex-col">
      <div className="flex items-center mb-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
        <span
          title={tooltipText}
          // data-tooltip-id="config-tooltip"
          // data-tooltip-content={tooltipText}
          className="ml-2 z-50 text-gray-400 hover:text-gray-600 "
        >
          <InformationCircleIcon className="h-4 w-4" />
        </span>
      </div>
      <input
        id={id}
        type="number"
        className="w-full px-3 py-2 text-gray-800 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
        value={value ?? ""}
        onChange={onChange}
        {...props}
      />
    </div>
  );
};

interface RoleValidationConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: RoleValidationConfig | null;
  setConfig: React.Dispatch<React.SetStateAction<RoleValidationConfig | null>>;
  onSave: () => void;
}

export default function RoleValidationConfigModal({
  isOpen,
  onClose,
  config,
  setConfig,
  onSave,
}: RoleValidationConfigModalProps) {
  if (!isOpen) {
    return null;
  }

  const STANDARD_VALUES = {
    ugMinStartAge: 17,
    ugFirstMaxStartAge: 21,
    ugMinDurationYears: 3,
    ugMaxDurationYears: 4,
    pgMaxDurationYears: 5,
    workMaxStartAge: 25,
    maxGapYears: 3,
    maxGapYearsBelowAge: 2,
    gapAgeThreshold: 25,
  };

  // Cleared values
  const CLEARED_VALUES = {
    ugMinStartAge: null,
    ugFirstMaxStartAge: null,
    ugMinDurationYears: null,
    ugMaxDurationYears: null,
    pgMaxDurationYears: null,
    workMaxStartAge: null,
    maxGapYears: null,
    maxGapYearsBelowAge: null,
    gapAgeThreshold: null,
  };

  const handleSetToStandard = () => {
    setConfig((prevConfig) =>
      prevConfig ? { ...prevConfig, ...STANDARD_VALUES } : null
    );
  };

  const handleClearAll = () => {
    setConfig((prevConfig) =>
      prevConfig ? { ...prevConfig, ...CLEARED_VALUES } : null
    );
  };

  const handleConfigChange = (
    key: keyof RoleValidationConfig,
    value: string
  ) => {
    setConfig((prevConfig) => {
      if (!prevConfig) return null;
      return {
        ...prevConfig,
        [key]: value === "" ? null : Number(value),
      };
    });
  };

  const ugFields: Array<Omit<ConfigInputProps, "value" | "onChange">> = [
    {
      id: "ugMinStartAge",
      label: "UG Min Start Age",
      tooltipText:
        "The minimum age a candidate can be when they start their undergraduate degree.",
    },
    {
      id: "ugFirstMaxStartAge",
      label: "UG First Max Start Age",
      tooltipText:
        "The maximum age for a candidate starting their first undergraduate degree.",
    },
    {
      id: "ugMinDurationYears",
      label: "UG Min Duration (years)",
      step: 0.1,
      tooltipText:
        "The minimum allowed duration in years for an undergraduate degree.",
    },
    {
      id: "ugMaxDurationYears",
      label: "UG Max Duration (years)",
      step: 0.1,
      tooltipText:
        "The maximum allowed duration in years for an undergraduate degree.",
    },
  ];

  const pgFields: Array<Omit<ConfigInputProps, "value" | "onChange">> = [
    {
      id: "pgMaxDurationYears",
      label: "PG Max Duration (years)",
      step: 0.1,
      tooltipText:
        "The maximum allowed duration in years for a postgraduate degree.",
    },
  ];

  const workAndGapFields: Array<Omit<ConfigInputProps, "value" | "onChange">> =
    [
      {
        id: "workMaxStartAge",
        label: "Work Max Start Age",
        tooltipText:
          "The maximum age a candidate can be when they start their first job.",
      },
      {
        id: "maxGapYears",
        label: "Max Gap (years)",
        step: 0.1,
        tooltipText:
          "The maximum allowed gap in years between education or work experiences.",
      },
      {
        id: "maxGapYearsBelowAge",
        label: "Max Gap Below Age (years)",
        step: 0.1,
        tooltipText:
          "A different maximum gap limit for candidates below a certain age threshold.",
      },
      {
        id: "gapAgeThreshold",
        label: "Gap Age Threshold",
        tooltipText:
          'The age threshold used to apply the "Max Gap Below Age" rule.',
      },
    ];

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="bg-white relative rounded-xl shadow-2xl w-[48rem] max-w-[95vw] p-6 m-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800">
              Role Validation Configuration
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 rounded-full p-1 hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6  space-y-6 max-h-[70vh] overflow-y-auto pr-2">
            {/* Undergraduate Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Undergraduate (UG)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {ugFields.map((field) => (
                  <ConfigInput
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    tooltipText={field.tooltipText}
                    value={config?.[field.id]}
                    onChange={(e) =>
                      handleConfigChange(field.id, e.target.value)
                    }
                    step={field.step || 1}
                  />
                ))}
              </div>
            </section>

            {/* Postgraduate Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Postgraduate (PG)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {pgFields.map((field) => (
                  <ConfigInput
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    tooltipText={field.tooltipText}
                    value={config?.[field.id]}
                    onChange={(e) =>
                      handleConfigChange(field.id, e.target.value)
                    }
                    step={field.step || 1}
                  />
                ))}
              </div>
            </section>

            {/* Work & Gaps Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Work & Career Gaps
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {workAndGapFields.map((field) => (
                  <ConfigInput
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    tooltipText={field.tooltipText}
                    value={config?.[field.id]}
                    onChange={(e) =>
                      handleConfigChange(field.id, e.target.value)
                    }
                    step={field.step || 1}
                  />
                ))}
              </div>
            </section>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 mt-6 border-t border-gray-200">
            <div className="flex gap-4">
              <button
                className="px-5 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-200"
                onClick={handleSetToStandard}
              >
                Set to Standard
              </button>
              <button
                className="px-5 py-2 text-sm font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 transition-all duration-200"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </div>
            <div className="flex gap-4">
              <button
                className="px-5 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-200"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition-all duration-200"
                onClick={onSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      <Tooltip
        id="config-tooltip"
        place="top"
        className="max-w-xs text-center"
      />
    </>
  );
}
